import { ref, reactive, onMounted, onUnmounted, computed, watch, type ComputedRef } from "vue";

export interface UseCarouselOptions {
  // кол-во элементов
  itemCount: number | ComputedRef<number>;
  // процент ширины для переключения (по умолчанию 0.2 = 20%)
  threshold?: number;
  // масштабирование
  scale?: boolean;
  // отступ между элементами в пикселях
  gap?: number;
}

const getClientX = (e: TouchEvent | MouseEvent): number => {
  return "touches" in e ? e.touches[0].clientX : e.clientX;
};

export function useCarousel(options: UseCarouselOptions) {
  const { itemCount: itemCountOption, threshold = 0.2, scale = true, gap = 0 } = options;
  const itemCount = computed(() => (typeof itemCountOption === "number" ? itemCountOption : itemCountOption.value));

  const listRef = ref<HTMLElement | null>(null);
  const wrapperRef = ref<HTMLElement | null>(null);
  const dragTranslate = ref(0);

  const state = reactive({
    activeIndex: 0,
    isDragging: false,
    startX: 0,
    currentTranslate: 0,
  });

  const updateCarouselPosition = () => {
    if (!listRef.value || !wrapperRef.value) return;

    const containerWidth = wrapperRef.value.offsetWidth;
    const itemWidth = containerWidth + gap;
    const translateX = -state.activeIndex * itemWidth;
    state.currentTranslate = translateX;

    if (listRef.value) {
      listRef.value.style.transition = "transform 0.4s cubic-bezier(0.2, 1.25, 0.6, 1)";
    }

    listRef.value.style.transform = `translateX(${translateX}px)`;
  };

  const goToIndex = (index: number) => {
    if (index < 0 || index >= itemCount.value) return;

    state.activeIndex = index;
    updateCarouselPosition();
  };

  watch(itemCount, (newCount) => {
    if (state.activeIndex >= newCount && newCount > 0) {
      state.activeIndex = newCount - 1;
      updateCarouselPosition();
    } else if (newCount === 0) {
      state.activeIndex = 0;
      updateCarouselPosition();
    }
  });

  const handleStart = (e: TouchEvent | MouseEvent) => {
    state.isDragging = true;
    state.startX = getClientX(e);
    dragTranslate.value = state.currentTranslate;

    if (listRef.value) listRef.value.style.transition = "none";
  };

  const handleMove = (e: TouchEvent | MouseEvent) => {
    if (!state.isDragging || !listRef.value) return;

    const currentX = getClientX(e);
    const diff = currentX - state.startX;
    const translateX = state.currentTranslate + diff;

    dragTranslate.value = translateX;
    listRef.value.style.transform = `translateX(${translateX}px)`;
  };

  const handleEnd = (e: TouchEvent | MouseEvent) => {
    if (!state.isDragging || !wrapperRef.value) return;

    const containerWidth = wrapperRef.value.offsetWidth;
    const endX = "changedTouches" in e ? e.changedTouches[0].clientX : e.clientX;
    const diff = state.startX - endX;
    const swipeThreshold = containerWidth * threshold;

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0 && state.activeIndex < itemCount.value - 1) {
        goToIndex(state.activeIndex + 1);
      } else if (diff < 0 && state.activeIndex > 0) {
        goToIndex(state.activeIndex - 1);
      } else {
        updateCarouselPosition();
      }
    } else {
      updateCarouselPosition();
    }

    state.isDragging = false;
    dragTranslate.value = 0;
  };

  // Вычисление масштаба для элемента
  const getItemScale = (index: number) => {
    if (!listRef.value || !wrapperRef.value) return 1;

    const containerWidth = wrapperRef.value.offsetWidth;
    const itemWidth = containerWidth + gap;
    const currentPosition = state.isDragging ? dragTranslate.value : state.currentTranslate;

    // Позиция центра элемента относительно центра контейнера
    const elementLeftEdge = index * itemWidth + currentPosition;
    const elementCenter = elementLeftEdge + containerWidth / 2;
    const containerCenter = containerWidth / 2;
    const distanceFromCenter = Math.abs(elementCenter - containerCenter);

    // Максимальное расстояние (половина ширины контейнера) = масштаб 0.8
    const maxDistance = containerWidth / 2;
    const normalizedDistance = Math.min(distanceFromCenter / maxDistance, 1);

    // Масштаб от 1 (в центре) до 0.8 (на краю)
    const scale = 1 - normalizedDistance * 0.2;
    return Math.max(scale, 0.8);
  };

  // Получение стилей для элемента карусели
  const getItemStyle = (index: number) => {
    return {
      flex: "0 0 100%",
      ...(scale ? { transform: `scale(${getItemScale(index)})` } : {}),
      ...(scale ? { transformOrigin: "center" } : {}),
      transition: "transform 0.4s cubic-bezier(0.2, 1.25, 0.6, 1)",
      willChange: "transform",
    };
  };

  onMounted(() => {
    if (listRef.value) {
      listRef.value.addEventListener("mousedown", handleStart);
      document.addEventListener("mousemove", handleMove);
      document.addEventListener("mouseup", handleEnd);
    }
    setTimeout(updateCarouselPosition, 0);
  });

  onUnmounted(() => {
    if (listRef.value) {
      listRef.value.removeEventListener("mousedown", handleStart);
    }
    document.removeEventListener("mousemove", handleMove);
    document.removeEventListener("mouseup", handleEnd);
  });

  return {
    state,
    listRef,
    wrapperRef,
    goToIndex,
    handleStart,
    handleMove,
    handleEnd,
    getItemScale,
    getItemStyle,
  };
}
